import { useIntl } from 'react-intl'
import { AsideMenuItemWithSub } from './AsideMenuItemWithSub'
import { AsideMenuItem } from './AsideMenuItem'
import { useState, useEffect, useMemo, useCallback } from 'react'
import axios from 'axios'
import { FiX, FiSearch, FiGrid, FiUser } from 'react-icons/fi'
import { isEmpty } from 'lodash'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { isMenuActive } from './menuActive'
import { REPORT_MENU_API } from 'src/app/modules/reportMenu/redux/service'
import { getMenuIcon } from './menuIcons'

interface EmbedConfig {
  id: string
  dashboard_title: string
  dashboard_slug: string
  embed_uuid: string
  is_active: boolean
  roles?: string[]
}

interface MenuRoute {
  title: string
  route: string
  subRoutes?: MenuRoute[]
}

const matches = (title: string | undefined, query: string) =>
  (title || '').toLowerCase().includes(query)

/** Parent icon, resolved locally from the route/title (backend `iconClass` is ignored). */
const ParentIcon = ({ route, title }: { route?: string; title?: string }) => {
  const Icon = getMenuIcon(route, title)
  return <Icon />
}

export function AsideMenuMain({ data }: any) {
  const intl = useIntl()

  const { pathname } = useLocation()
  const [searchVal, setSearchVal] = useState('')
  const [supersetMenus, setSupersetMenus] = useState<EmbedConfig[]>([])

  const currentRole: string = useSelector((state: any) => state.auth?.rolePermissions?.name) || ''
  const isAdmin: boolean =
    useSelector((state: any) => state.auth?.user?.data?.userName === 'superadmin') || false

  useEffect(() => {
    if (!currentRole && !isAdmin) return
    axios
      .get(REPORT_MENU_API, { params: { page: 1, limit: 9999 } })
      .then((res) => {
        const list: EmbedConfig[] =
          res?.data?.data?.superset_embed_configs || res?.data?.data || []
        const visible = isAdmin
          ? list.filter((c) => c.is_active)
          : list.filter(
            (c) =>
              c.is_active &&
              Array.isArray(c.roles) &&
              c.roles.includes(currentRole)
          )
        setSupersetMenus(visible)
      })
      .catch(() => { })
  }, [currentRole, isAdmin])

  // The authorized menu tree, with the dynamic Superset dashboards merged in as
  // children of the Superset parent. This is the single list the menu renders
  // AND searches, so a search result can never be a module the user can't open.
  const menuItems: MenuRoute[] = useMemo(() => {
    const source: MenuRoute[] = Array.isArray(data) ? data : []
    return source.map((routes) => {
      const isSupersetParent =
        routes.route?.includes('superset-report') ||
        routes.title?.toLowerCase()?.includes('superset')
      const dynamicSubRoutes = isSupersetParent
        ? supersetMenus.map((m) => ({
          route: `/superset-report/${m.dashboard_slug}`,
          title: m.dashboard_title,
        }))
        : []
      return { ...routes, subRoutes: [...(routes.subRoutes || []), ...dynamicSubRoutes] }
    })
  }, [data, supersetMenus])

  const query = searchVal.trim().toLowerCase()

  // A parent matches -> keep it with all of its children.
  // Only children match -> keep the parent, narrowed to the matching children.
  const visibleItems: MenuRoute[] = useMemo(() => {
    if (!query) return menuItems
    return menuItems.reduce((acc: MenuRoute[], item) => {
      if (matches(item.title, query)) {
        acc.push(item)
        return acc
      }
      const matchedChildren = (item.subRoutes || []).filter((child) =>
        matches(child.title, query)
      )
      if (matchedChildren.length) {
        acc.push({ ...item, subRoutes: matchedChildren })
      }
      return acc
    }, [])
  }, [menuItems, query])

  // --- single-open accordion -------------------------------------------
  // Exactly one group is expanded at a time, so opening one collapses the rest.
  const [openKey, setOpenKey] = useState<string | null>(null)

  const keyOf = (item: MenuRoute, index: number) => item.route || item.title || String(index)

  // The group that owns the current page. A child can sit under a different
  // route prefix than its parent's alias, so the parent's own alias is not
  // enough -- the children have to be checked too, which only this level can do.
  const activeGroupKey: string | null = useMemo(() => {
    const activeItem = menuItems.find(
      (item) =>
        (item.subRoutes || []).length > 0 &&
        (isMenuActive(pathname, item.route) ||
          (item.subRoutes || []).some((child) => isMenuActive(pathname, child.route)))
    )
    return activeItem ? activeItem.route || activeItem.title : null
  }, [pathname, menuItems])

  // Open that group on mount, on navigation, and again once a search is cleared.
  useEffect(() => {
    if (query || !activeGroupKey) return
    setOpenKey(activeGroupKey)
  }, [activeGroupKey, query])

  // While searching, expand the first group that has matches so a result is
  // visible without a click -- still one group at a time.
  useEffect(() => {
    if (!query) return
    const firstGroup = visibleItems.find((item) => (item.subRoutes || []).length > 0)
    setOpenKey(firstGroup ? firstGroup.route || firstGroup.title : null)
  }, [query, visibleItems])

  const toggleGroup = useCallback((key: string) => {
    setOpenKey((prev) => (prev === key ? null : key))
  }, [])

  return (
    <>
      {/* Fixed block: shortcuts and the search field stay put ... */}
      <div className='gibl-menu-head'>
        <AsideMenuItem
          to='/dashboard'
          title={intl.formatMessage({ id: 'MENU.DASHBOARD' })}
          iconNode={<FiGrid />}
        />
        <AsideMenuItem to='/profile' title={'Profile'} iconNode={<FiUser />} />

        <div className='gibl-menu-separator'></div>

        <div className='gibl-menu-search'>
          <span className='gibl-menu-search__icon'>
            <FiSearch size={16} />
          </span>
          <input
            type='text'
            placeholder='Search menu'
            className='gibl-menu-search__input'
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
            data-cy='nav-search'
          />
          {searchVal && (
            <button
              type='button'
              className='gibl-menu-search__clear'
              onClick={() => setSearchVal('')}
              aria-label='Clear search'
            >
              <FiX size={16} />
            </button>
          )}
        </div>
      </div>

      {/* ... everything after the search bar scrolls. */}
      <div className='gibl-menu-list'>
        <div className='gibl-menu-section'>Menu</div>

        {isEmpty(menuItems) ? (
          <p className='gibl-menu-empty'>Loading...</p>
        ) : !isEmpty(visibleItems) ? (
          visibleItems.map((routes: MenuRoute, i: number) => {
            const subRoutes = routes.subRoutes || []
            const hasChild = subRoutes.length > 0
            const key = keyOf(routes, i)

            return (
              <div key={key}>
                {!hasChild && (
                  <AsideMenuItem
                    to={routes.route}
                    title={routes.title}
                    iconNode={<ParentIcon route={routes.route} title={routes.title} />}
                  />
                )}

                {hasChild && (
                  <AsideMenuItemWithSub
                    to={routes.route}
                    title={routes.title}
                    iconNode={<ParentIcon route={routes.route} title={routes.title} />}
                    isOpen={openKey === key}
                    onToggle={() => toggleGroup(key)}
                    isBranchActive={activeGroupKey === key}
                  >
                    {subRoutes.map((subRoute: MenuRoute, j: number) => (
                      <AsideMenuItem
                        to={subRoute.route}
                        title={subRoute.title}
                        variant='child'
                        key={subRoute.route || j}
                      />
                    ))}
                  </AsideMenuItemWithSub>
                )}
              </div>
            )
          })
        ) : (
          <p className='gibl-menu-empty'>No menu matches “{searchVal.trim()}”</p>
        )}
      </div>
    </>
  )
}
